// src/hooks/useJourneyLogic.js
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { shuffleArray } from "../utils/helpers";
import { AVAILABLE_SURAHS } from "../constants/surahs";

// --- Weighted Random Picker (Kept for standalone/future use) ---
function pickWeightedRandomSurah(surahs) {
    const totalWeight = surahs.reduce((sum, s) => sum + (s.weight || 1), 0);
    const rand = Math.random() * totalWeight;
    let running = 0;
    for (const surah of surahs) {
        running += surah.weight || 1;
        if (rand <= running) return surah;
    }
    return surahs[0]; // fallback
}

const useJourneyLogic = ({
                             activityType,
                             gameSpecificEndCondition = false,
                             gameLength,
                             onComplete,
                             currentSurahId // This is the ID passed from JourneyPage.jsx config
                         }) => {
    const { user } = useAuth();
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;

    // --- Journey State ---
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [journeyScore, setJourneyScore] = useState(0);
    const [isGameEnded, setIsGameEnded] = useState(false);

    // --- Loading State ---
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [error, setError] = useState(null);

    // --- Data Fetching Logic ---
    const fetchQuestionsFromBackend = useCallback(
        async (surahId = null) => {
            const cleanedUrl = backendApiUrl.endsWith("/")
                ? backendApiUrl.slice(0, -1)
                : backendApiUrl;

            let apiUrl = `${cleanedUrl}/${activityType}`;

            // ✅ FIX 1: Ensure the Flashcards API call is correct and sends the Surah ID
            if (activityType === "flashcards" && surahId) {
                // Assuming your backend endpoint supports fetching ALL flashcards for a surah ID
                apiUrl = `${cleanedUrl}/flashcards/surah/${surahId}`;
            } else if (activityType === "originalQuiz") apiUrl = `${cleanedUrl}/quiz`;
            else if (activityType === "ayah-blanks") apiUrl = `${cleanedUrl}/ayah-blanks`;
            else if (activityType === "halalvsharam") apiUrl = `${cleanedUrl}/halalvsharam`;

            console.log(`📡 Journey fetching from: ${apiUrl}`);
            // ⚠️ Potential Issue: If your backend limits the result here (e.g., to 20),
            // the problem is on the server, not in this React code.
            // Assuming the server returns *all* questions for the given Surah ID.
            const res = await axios.get(apiUrl);
            return res.data;
        },
        [backendApiUrl, activityType]
    );

    const loadQuizQuestions = useCallback(
        async (preloadedData = null, doShuffle = true) => {
            setLoadingQuestions(true);
            setError(null);
            setCurrent(0);
            setQuestions([]);

            if (!user) {
                setLoadingQuestions(false);
                setError("Please login to access the game.");
                return;
            }

            try {
                let chosenSurahId = currentSurahId; // Start with the ID passed from the component

                // ❌ REMOVE OLD RANDOMIZATION LOGIC:
                // The JourneyPage now provides the config.surah, so we should trust it.
                /*
                if (activityType === "flashcards") {
                    const randomSurah = pickWeightedRandomSurah(AVAILABLE_SURAHS);
                    chosenSurahId = randomSurah.id;
                    console.log("📖 Flashcards random Surah chosen:", randomSurah.name);
                }
                */

                // ✅ Update: If no surah ID is provided (e.g., in standalone mode),
                // then fall back to the random picker.
                if (activityType === "flashcards" && !chosenSurahId) {
                    const randomSurah = pickWeightedRandomSurah(AVAILABLE_SURAHS);
                    chosenSurahId = randomSurah.id;
                    console.log("📖 Flashcards random Surah chosen:", randomSurah.name);
                }


                const allQuestions =
                    preloadedData || (await fetchQuestionsFromBackend(chosenSurahId));

                if (!Array.isArray(allQuestions) || allQuestions.length === 0) {
                    setError("No questions available for this activity.");
                    setLoadingQuestions(false);
                    return;
                }

                let questionsToUse = [...allQuestions];

                // --- Handle Flashcards ---
                if (activityType === "flashcards") {
                    // ✅ FIX 2: Confirm we use ALL questions for the full surah length.
                    console.log(
                        `⚡ Flashcards Journey loaded Surah with ${questionsToUse.length} questions (FULL LENGTH)`
                    );
                    // No slicing here! The length is whatever the backend provided.
                } else if (doShuffle) {
                    // Other activities: shuffle + slice to the required gameLength
                    questionsToUse = shuffleArray(allQuestions).slice(0, gameLength);
                }

                // --- Process Questions ---
                const questionsWithShuffledOptions = questionsToUse.map((q) => {
                    let correctAnswerValue;

                    if (activityType === "flashcards") {
                        // Flashcards: prioritize meaning fields
                        correctAnswerValue =
                            q.correctMeaning || q.meaning || q.correctAnswerText;
                    } else {
                        correctAnswerValue =
                            q.correctAnswerText ||
                            q.answer ||
                            q.options?.[q.correctAnswer] ||
                            q.options?.[0];
                    }

                    if (!correctAnswerValue) {
                        console.warn("No correct answer found for question:", q._id);
                        correctAnswerValue = "Unknown";
                    }

                    // Shuffle options if they exist
                    let mappedOptions = [];
                    if (q.options && Array.isArray(q.options)) {
                        mappedOptions = shuffleArray(
                            q.options.map((o) => (typeof o === "string" ? o : o.text))
                        );
                    }

                    return {
                        ...q,
                        options: mappedOptions,
                        correctAnswerText: correctAnswerValue,
                        correctMeaning: q.meaning,
                        correctArabicWord: q.correctArabicWord || q.arabicWord,
                        transliteration: q.transliteration,
                    };
                });

                setQuestions(questionsWithShuffledOptions);
            } catch (err) {
                console.error("❌ Error loading journey questions:", err);
                setError(err.message || "Failed to load activity. Please try again.");
            } finally {
                setLoadingQuestions(false);
            }
        },
        [fetchQuestionsFromBackend, gameLength, user, currentSurahId, activityType]
    );

    // --- Initial Load Effect ---
    useEffect(() => {
        loadQuizQuestions();
    }, [loadQuizQuestions]);

    // --- Core Completion Logic ---
    useEffect(() => {
        if (questions.length === 0) return;

        // Flashcards in journey mode should only complete when all cards are done.
        const endConditionMet = activityType === "flashcards" ? current >= questions.length : gameSpecificEndCondition;

        if (endConditionMet && !isGameEnded) {
            setIsGameEnded(true);

            // 💡 Ensure flashcards calls onComplete when current reaches questions.length
            if (typeof onComplete === "function") {
                onComplete(activityType);
            }
        }
    }, [gameSpecificEndCondition, isGameEnded, onComplete, activityType, questions.length, current]);

    // --- Journey Handlers ---
    const handleCorrectAttempt = useCallback(() => {
        setJourneyScore((prev) => prev + 1);
    }, []);

    const handleIncorrectAttempt = useCallback(() => {}, []);

    const handleAdvance = useCallback(() => {
        setCurrent((prev) => prev + 1);
    }, []);

    return {
        questions,
        current,
        setCurrent,
        journeyScore,
        isGameEnded,
        loadingQuestions,
        error,
        loadQuizQuestions,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        handleAdvance,
    };
};

export default useJourneyLogic;